import type { TrainingModuleMetadata } from "@/lib/api/training-simulator/training-simulator";
import { Moodies, Scribbles } from "@somethings-ui/core/assets";

/**
 * Maps a module's `metadata.theme.color` brand token to a `colorsV2.brand`
 * Tailwind background class. Falls back to a position-based brand palette when
 * no theme is provided, matching the Figma module order.
 */
const BRAND_BG_BY_TOKEN: Record<string, string> = {
  forest: "bg-brand-forest",
  sky: "bg-brand-sky",
  sunshine: "bg-brand-sunshine",
  apricot: "bg-brand-apricot",
  blush: "bg-brand-blush",
  lime: "bg-brand-lime",
  tangelo: "bg-brand-tangelo",
};

const FALLBACK_PALETTE = [
  "bg-brand-forest",
  "bg-brand-sky",
  "bg-brand-sunshine",
  "bg-brand-apricot",
  "bg-brand-blush",
  "bg-brand-lime",
] as const;

const FALLBACK_ASSETS = [
  Scribbles.blackSwoosh,
  Scribbles.blackStarburst,
  Scribbles.blackSpiral,
  Scribbles.blackCloud,
  Moodies.awkwardBlack,
  Scribbles.blackSwoosh,
] as const;

const ASSET_BY_KEY: Record<string, (typeof FALLBACK_ASSETS)[number]> = {
  awkward: Moodies.awkwardBlack,
  awkwardblack: Moodies.awkwardBlack,
  blackcloud: Scribbles.blackCloud,
  blackspiral: Scribbles.blackSpiral,
  blackstarburst: Scribbles.blackStarburst,
  blackswoosh: Scribbles.blackSwoosh,
  cloud: Scribbles.blackCloud,
  spiral: Scribbles.blackSpiral,
  starburst: Scribbles.blackStarburst,
  swoosh: Scribbles.blackSwoosh,
};

export interface ModuleTheme {
  headerClassName: string;
  headerAsset: (typeof FALLBACK_ASSETS)[number];
}

interface ThemeableTrainingModule {
  position: number;
  metadata?: TrainingModuleMetadata | null;
}

export const getModuleTheme = (
  module: ThemeableTrainingModule,
): ModuleTheme => {
  const color = module.metadata?.theme?.color?.trim().toLowerCase();
  const assetKey = module.metadata?.theme?.assetKey
    ?.trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

  const result: ModuleTheme = {
    headerClassName: "",
    headerAsset: FALLBACK_ASSETS[0],
  };

  if (color && color in BRAND_BG_BY_TOKEN) {
    const mapped = BRAND_BG_BY_TOKEN[color];

    result.headerClassName = mapped;
  } else {
    const index = Math.max(0, module.position - 1) % FALLBACK_PALETTE.length;
    result.headerClassName = FALLBACK_PALETTE[index];
  }

  if (assetKey && assetKey in ASSET_BY_KEY) {
    result.headerAsset = ASSET_BY_KEY[assetKey];
  } else {
    const index = Math.max(0, module.position - 1) % FALLBACK_ASSETS.length;
    result.headerAsset = FALLBACK_ASSETS[index];
  }

  return result;
};
